import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon, ArrowDown01Icon } from '@hugeicons/core-free-icons'
import type { ChangeEvent } from 'react'

import type { OrganizationUser } from '@/features/organization/types'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface ReassignDirectReportsStepProps {
  open: boolean
  userName: string
  userId: string
  directReports: Array<OrganizationUser>
  allCandidates: Array<OrganizationUser>
  managerIds: Set<string>
  isAdmin: boolean
  isLoading: boolean
  assignments: Record<string, string | null>
  onAssignmentChange: (drId: string, managerId: string | null) => void
  grantManagerIds: Array<string>
  onGrantManagerIdsChange: (ids: Array<string>) => void
  assignedCount: number
  allAssigned: boolean
  executableCount: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  onProceed: () => void
  onCancel: () => void
}

export function ReassignDirectReportsStep({
  open,
  userName,
  userId,
  directReports,
  allCandidates,
  managerIds,
  isAdmin,
  isLoading,
  assignments,
  onAssignmentChange,
  grantManagerIds,
  onGrantManagerIdsChange,
  assignedCount,
  allAssigned,
  executableCount,
  onProceed,
  onCancel,
}: ReassignDirectReportsStepProps) {
  // Local state for popover open/search — only used within this step
  const [searchMap, setSearchMap] = useState<Record<string, string>>({})
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({})

  function setSearchFor(drId: string, value: string) {
    setSearchMap((s) => ({ ...s, [drId]: value }))
  }

  function setOpenFor(drId: string, openState: boolean) {
    setOpenMap((s) => ({ ...s, [drId]: openState }))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel()
      }}
    >
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Reassign Direct Reports</DialogTitle>
          <DialogDescription>
            Before removing {userName}, you must reassign their{' '}
            {directReports.length} direct report
            {directReports.length !== 1 ? 's' : ''} to another manager.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto max-h-[50vh]">
          <div className="space-y-3 py-2">
            {directReports.map((dr) => {
              const assignedCandidate = allCandidates.find(
                (u) => u.id === assignments[dr.id],
              )
              const isAssigned = !!assignments[dr.id]
              const candidateIsManager = assignedCandidate
                ? managerIds.has(assignedCandidate.id)
                : false

              return (
                <div
                  key={dr.id}
                  className={cn(
                    'flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg',
                    isAssigned
                      ? 'bg-primary/5 border-primary/20 dark:bg-primary/10 dark:border-primary/30'
                      : 'bg-background',
                  )}
                >
                  {/* Employee Info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <AvatarWithInitials
                      name={dr.name || ''}
                      src={dr.avatar}
                      className="h-10 w-10 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">
                        {dr.name || 'Unknown User'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {dr.roles[0]?.name || 'No role'}
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="hidden sm:flex items-center text-muted-foreground">
                    <HugeiconsIcon
                      icon={ArrowRight01Icon}
                      className="h-5 w-5"
                    />
                  </div>

                  {/* Manager Selection */}
                  <div className="sm:w-64">
                    <Popover
                      open={!!openMap[dr.id]}
                      onOpenChange={(o) => setOpenFor(dr.id, o)}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant={isAssigned ? 'secondary' : 'outline'}
                          size="sm"
                          className={cn(
                            'w-full justify-between',
                            isAssigned
                              ? 'border-primary/40 dark:border-primary/50'
                              : '',
                          )}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isAssigned ? (
                              <>
                                <AvatarWithInitials
                                  name={assignedCandidate?.name || ''}
                                  src={assignedCandidate?.avatar}
                                  className="h-5 w-5 shrink-0"
                                  fallbackClassName="text-[10px]"
                                  fallbackBgColor="bg-primary text-primary-foreground"
                                />
                                <span className="truncate text-sm">
                                  {assignedCandidate?.name}
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                Select manager...
                              </span>
                            )}
                          </div>
                          <HugeiconsIcon
                            icon={ArrowDown01Icon}
                            className="h-3.5 w-3.5 shrink-0 opacity-50"
                          />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-0" align="end">
                        <Command>
                          <CommandInput
                            placeholder="Search users..."
                            value={searchMap[dr.id] || ''}
                            onValueChange={(val: string) =>
                              setSearchFor(dr.id, val)
                            }
                          />
                          <CommandList>
                            <CommandEmpty>No users found</CommandEmpty>
                            {allCandidates
                              .filter(
                                (c) =>
                                  c.name
                                    .toLowerCase()
                                    .includes(
                                      (searchMap[dr.id] || '').toLowerCase(),
                                    ) &&
                                  c.id !== userId && // Exclude the user being removed
                                  c.id !== dr.id, // Exclude the direct report themselves
                              )
                              .slice(0, 30)
                              .map((c) => (
                                <CommandItem
                                  key={c.id}
                                  onSelect={() => {
                                    onAssignmentChange(dr.id, c.id)
                                    setOpenFor(dr.id, false)
                                  }}
                                  className="py-2"
                                >
                                  <div className="flex items-center justify-between w-full gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <AvatarWithInitials
                                        name={c.name}
                                        src={c.avatar}
                                        className="h-7 w-7 shrink-0"
                                        fallbackClassName="text-xs"
                                        fallbackBgColor="bg-primary text-primary-foreground"
                                      />
                                      <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">
                                          {c.name}
                                        </div>
                                      </div>
                                    </div>
                                    <span
                                      className={cn(
                                        'text-xs px-2 py-0.5 rounded-full shrink-0',
                                        managerIds.has(c.id)
                                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                                      )}
                                    >
                                      {managerIds.has(c.id)
                                        ? 'Manager'
                                        : 'Employee'}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {/* Status */}
                    {isAssigned && (
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs">
                          {!candidateIsManager && isAdmin ? (
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={
                                  !!assignedCandidate?.id &&
                                  grantManagerIds.includes(assignedCandidate.id)
                                }
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (e.target.checked) {
                                    if (assignedCandidate?.id) {
                                      onGrantManagerIdsChange(
                                        Array.from(
                                          new Set([
                                            ...grantManagerIds,
                                            assignedCandidate.id,
                                          ]),
                                        ),
                                      )
                                    }
                                  } else {
                                    onGrantManagerIdsChange(
                                      grantManagerIds.filter(
                                        (id) => id !== assignedCandidate?.id,
                                      ),
                                    )
                                  }
                                }}
                                className="h-3 w-3"
                              />
                              <span className="text-amber-600 dark:text-amber-400">
                                Grant manager rights
                              </span>
                            </label>
                          ) : !candidateIsManager && !isAdmin ? (
                            <span className="text-destructive">
                              Not a manager - will skip
                            </span>
                          ) : (
                            <span className="text-primary">Ready</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => onAssignmentChange(dr.id, null)}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{assignedCount}</span>{' '}
            of{' '}
            <span className="font-medium text-foreground">
              {directReports.length}
            </span>{' '}
            assigned
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onProceed}
              disabled={!allAssigned || executableCount === 0}
            >
              Continue
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
